/*global navigator sessionStorage*/
import React, { useEffect, useMemo, useState } from 'react';
import { inject } from 'mobx-react';
import { PropTypes } from 'prop-types';

import {withConfig, withI18n} from 'spa/Components/Context';
import * as querystring from 'qs';
import classie from 'classie';
import Cookies from 'js-cookie';

import { IconSizes } from '@urbanmassage/component-library/lib/Theme/b2c-theme';
import { IconSpacingSizes } from '@urbanmassage/component-library/lib/Icons/Star/Star';
import Button, { ButtonSizeTypes } from '@urbanmassage/component-library/lib/Button/Button';
import Cancel from '@urbanmassage/component-library/lib/Icons/Cancel/Cancel';
import IconButton, { IconButtonSizes, IconButtonThemeTypes } from '@urbanmassage/component-library/lib/IconButton/IconButton';
import logo from 'spa/images/logo-yellow.svg';
import StarRatingGroup from '@urbanmassage/component-library/lib/StarRatingGroup/StarRatingGroup';
import Typography, { TypeVariants } from '@urbanmassage/component-library/lib/Typography/Typography';
import Download, {IconColorThemes} from '@urbanmassage/component-library/lib/Icons/Download/Download';

import './smartbanner.less';

// new smart banner cookie name
const DISMISSED_SMART_BANNER_COOKIE_NAME = 'dismissedsb';

const ContentIos = ({
  handleInstall,
  i18n,
  iosRating,
  iosReviewsCount,
  iosDownloadsCount,
}) => (
  <React.Fragment>
    <div className="smartbanner__content-wrapper">
      <div className="smartbanner__content-wrapper-img">
        <img src={logo} alt="Urban logo" />
      </div>
      <div>
        <Typography
          className="smartbanner__content-title"
          theme="white"
          variant={TypeVariants.bodySmall}
        >
          {i18n._x('Urban for iOS', 'smart banner title')}
        </Typography>

        <div className="smartbanner__ratings-wrapper">
          {iosRating && (
            <StarRatingGroup
              activeStars={iosRating}
              iconSize={IconSizes.xSmall}
              iconSpacing={IconSpacingSizes.small}
              includeRatingCount={iosReviewsCount || iosDownloadsCount}
              ratingCount={iosReviewsCount ? `(${iosReviewsCount})` : iosDownloadsCount}
              totalStars="5"
              includeSeparator={!!iosDownloadsCount && !iosReviewsCount}
            />
          )}
          {iosDownloadsCount && !iosReviewsCount && (
            <div className="smartbanner__downloads-icon-wrapper">
              <Download color={IconColorThemes.white} />
            </div>
          )}
        </div>
      </div>
    </div>
    <div className="smartbanner__install-btn-wrapper">
      <Button
        block
        onClick={handleInstall}
        size={ButtonSizeTypes.small}>
        {i18n._x('Install', 'smart banner')}
      </Button>
    </div>
  </React.Fragment>
);

const ContentAndroid = ({
  handleInstall,
  i18n,
  androidRating,
  androidReviewsCount,
  androidDownloadsCount,
}) => (
  <React.Fragment>
    <div className="smartbanner__content-wrapper">
      <div className="smartbanner__content-wrapper-img">
        <img src={logo} alt="Urban logo" />
      </div>
      <div>
        <Typography
          className="smartbanner__content-title"
          theme="white"
          variant={TypeVariants.bodySmall}
        >
          {i18n._x('Urban for Android', 'smart title')}
        </Typography>
        <div className="smartbanner__ratings-wrapper">
          {androidRating && (
            <StarRatingGroup
              activeStars={androidRating}
              iconSize={IconSizes.xSmall}
              iconSpacing={IconSpacingSizes.small}
              includeRatingCount={androidReviewsCount || androidDownloadsCount}
              ratingCount={androidReviewsCount ? `(${androidReviewsCount})` : androidDownloadsCount}
              totalStars="5"
              includeSeparator={!!androidDownloadsCount && !androidReviewsCount}
            />
          )}
          {androidDownloadsCount && !androidReviewsCount && (
            <div className="smartbanner__downloads-icon-wrapper">
              <Download color={IconColorThemes.white} />
            </div>
          )}
        </div>
      </div>
    </div>
    <div className="smartbanner__install-btn-wrapper">
      <Button
        block
        onClick={handleInstall}
        size={ButtonSizeTypes.small}
      >
        {i18n._x('Install', 'smart banner')}
      </Button>
    </div>
  </React.Fragment>
);

const Smartbanner = ({
  tracker,
  umDeviceIdentifier,
  userAgent,
  utmCookie,
}, {i18n, config}) => {
  const [isDismissed, setIsDismissed] = useState(false);
  const [isAndroid, setIsAndroid] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [showSmartBanner, setShowSmartBanner] = useState(false);
  const [iosRating, setIosRating] = useState(0);
  const [androidRating, setAndroidRating] = useState(0);
  const [iosReviewsCount, setIosReviewsCount] = useState(0);
  const [androidReviewsCount, setAndroidReviewsCount] = useState(0);
  const [iosDownloadsCount, setIosDownloadsCount] = useState(0);
  const [androidDownloadsCount, setAndroidDownloadsCount] = useState(0);

  const trackImpression = () => {
    const smartBannerImpression = sessionStorage.getItem('smartBannerImpression');
    if (!smartBannerImpression) {
      tracker.track('Smart Banner Impression', {
        category: 'Smart Banner',
        platform: isAndroid ? 'android' : 'ios',
      });
      sessionStorage.setItem('smartBannerImpression', 'true');
    }
  };

  const checkIfDismissed = () => {
    const dismissedSmartBanner = Cookies.get(DISMISSED_SMART_BANNER_COOKIE_NAME);
    if (dismissedSmartBanner) {
      setIsDismissed(true);
    }
  };

  const adjustUrl = useMemo(() => {
    const downloadAppsLink = config.getValue('locale.adjust-app-smartbanner-url', i18n.umRegion);

    let queryObject = {
      umDeviceIdentifier,
    };

    if (utmCookie) {
      queryObject = Object.assign(utmCookie, queryObject);
    }

    let urlSeparator = '?';

    if (downloadAppsLink) {
      urlSeparator = downloadAppsLink.includes('?') ? '&' : '?';
    }

    return `${downloadAppsLink}${urlSeparator}label=${encodeURIComponent(querystring.stringify(queryObject))}`;
  }, [i18n, umDeviceIdentifier, utmCookie]);

  const handleDismiss = () => {
    tracker.track('Smart Banner Dismissed', {
      category: 'Smart Banner',
      platform: isAndroid ? 'android' : 'ios',
    });

    Cookies.set(DISMISSED_SMART_BANNER_COOKIE_NAME, 'true', { expires: 90 });
    setIsDismissed(true);
  };

  const handleInstall = () => {
    tracker.track('Smart Banner Clicked', {
      category: 'Smart Banner',
      platform: isAndroid ? 'android' : 'ios',
    });

    Cookies.set(DISMISSED_SMART_BANNER_COOKIE_NAME, 'true', { expires: 90 });
    setTimeout(() => {
      window.location.href = adjustUrl;
    }, 300);
  };

  useEffect(() => {
    setIsAndroid(/Android/i.test(userAgent));
    setIsIOS(/iPhone|iPad|iPod/i.test(userAgent));
  }, [userAgent]);

  useEffect(() => {
    if (!isAndroid && !isIOS) return;
    if (isDismissed) {
      setShowSmartBanner(false);
      classie.remove(document.body, 'with-new-smart-banner');
      return;
    }
    setShowSmartBanner(true);
    classie.add(document.body, 'with-new-smart-banner');
  }, [isAndroid, isIOS, isDismissed]);

  useEffect(() => {
    checkIfDismissed();
    trackImpression();

    setIosRating(config.getValue('locale.smartbanner-apple-store-rating', i18n.umRegion));
    setIosReviewsCount(config.getValue('locale.smartbanner-apple-store-reviews-count', i18n.umRegion));
    setIosDownloadsCount(config.getValue('locale.smartbanner-apple-downloads-count', i18n.umRegion));
    setAndroidRating(config.getValue('locale.smartbanner-google-playstore-rating', i18n.umRegion));
    setAndroidReviewsCount(config.getValue('locale.smartbanner-google-reviews-count', i18n.umRegion));
    setAndroidDownloadsCount(config.getValue('locale.smartbanner-google-downloads-count', i18n.umRegion));
    return () => {
      classie.remove(document.body, 'with-new-smart-banner');
    };
  }, []);


  if (typeof navigator === 'undefined') return null;
  if (!showSmartBanner) return null;

  return (
    <div className="smartbanner">
      <div className="smartbanner__inner">
        <div className="smartbanner__close-icon-wrapper">
          <IconButton
            ariaLabel="Dismiss smart banner"
            onClick={handleDismiss}
            showBackground={false}
            showBorder={false}
            size={IconButtonSizes.small}
            theme={IconButtonThemeTypes.success}
          >
            <Cancel />
          </IconButton>
        </div>
        {isIOS && <ContentIos
          handleInstall={handleInstall}
          i18n={i18n}
          iosRating={iosRating}
          iosReviewsCount={iosReviewsCount}
          iosDownloadsCount={iosDownloadsCount}
        />}
        {isAndroid && <ContentAndroid
          handleInstall={handleInstall}
          i18n={i18n}
          androidRating={androidRating}
          androidReviewsCount={androidReviewsCount}
          androidDownloadsCount={androidDownloadsCount}
        />}
      </div>
    </div>
  );
};

Smartbanner.propTypes = {
  tracker: PropTypes.shape({
    track: PropTypes.func.isRequired,
  }).isRequired,
  umDeviceIdentifier: PropTypes.string,
  userAgent: PropTypes.string.isRequired,
  utmCookie: PropTypes.object,
};

ContentIos.propTypes = {
  i18n: PropTypes.shape({
    _x: PropTypes.func.isRequired,
  }).isRequired,
  handleInstall: PropTypes.func.isRequired,
  iosRating: PropTypes.number,
  iosReviewsCount: PropTypes.string,
  iosDownloadsCount: PropTypes.string,
};

ContentAndroid.propTypes = {
  i18n: PropTypes.shape({
    _x: PropTypes.func.isRequired,
  }).isRequired,
  handleInstall: PropTypes.func.isRequired,
  androidRating: PropTypes.number,
  androidReviewsCount: PropTypes.string,
  androidDownloadsCount: PropTypes.string,
};

export default inject(({
  store: {
    tracker,
    umDeviceIdentifier,
    userAgent,
    utmCookie,
  },
}) => ({
  tracker,
  umDeviceIdentifier,
  userAgent,
  utmCookie,
}))(withConfig(withI18n(Smartbanner)));



// WEBPACK FOOTER //
// ./spa/Components/Smartbanner/Smartbanner.jsx